import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PaymentIcon from '@mui/icons-material/Payment';
import CancelIcon from '@mui/icons-material/Cancel';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { useAuth } from '../../context/AuthContext';
import { getPedidos, Pedido, getEstadoLabel, getEstadoColor } from '../../services/pedidos';

const OrderCard = styled(Card)({
  borderRadius: '15px',
  boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
  border: '1px solid rgba(46, 90, 51, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
  },
});

const formatPrice = (price: number | string | null | undefined) => {
  // Manejar valores nulos o indefinidos
  if (price === null || price === undefined || price === '') {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(0);
  }
  
  const numPrice = typeof price === 'string' ? parseFloat(price) : price;
  
  // Verificar si es un número válido
  if (isNaN(numPrice)) {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(0);
  }
  
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0
  }).format(numPrice);
};

const getStatusIcon = (estado: string, estadoPago?: string, pagado?: boolean) => {
  // Si está pagado y aprobado, mostrar check verde
  if (pagado && estadoPago === 'aprobado') {
    return <CheckCircleIcon sx={{ color: '#4caf50' }} />;
  }
  // Si solo está procesando (no pagado aún)
  if (estadoPago === 'procesando' && !pagado) {
    return <AccessTimeIcon sx={{ color: '#ff9800' }} />;
  }
  
  switch (estado) {
    case 'pendiente':
    case 'confirmado':
      return <AccessTimeIcon />;
    case 'entregado':
      return <CheckCircleIcon />;
    case 'cancelado':
      return <CancelIcon />;
    default:
      return <AccessTimeIcon />;
  }
};

const getStatusColorBg = (estado: string, estadoPago?: string, pagado?: boolean) => {
  // Si está pagado y aprobado, usar color del estado del pedido
  if (pagado && estadoPago === 'aprobado') {
    switch (estado) {
      case 'pendiente': return '#ffa726';
      case 'confirmado': return '#2196f3';
      case 'preparando': return '#9c27b0';
      case 'enviado': return '#00bcd4';
      case 'entregado': return '#4caf50';
      case 'cancelado': return '#757575';
      default: return '#2196f3';
    }
  }
  // Si solo está procesando (no pagado aún)
  if (estadoPago === 'procesando' && !pagado) return '#ff9800';
  
  switch (estado) {
    case 'pendiente': return '#ffa726';
    case 'confirmado': return '#2196f3';
    case 'preparando': return '#9c27b0';
    case 'enviado': return '#00bcd4';
    case 'entregado': return '#4caf50';
    case 'cancelado': return '#757575';
    default: return '#757575';
  }
};

const getStatusText = (estado: string, estadoPago?: string, pagado?: boolean) => {
  // Si está pagado y aprobado, mostrar estado del pedido
  if (pagado && estadoPago === 'aprobado') {
    const estadoMap: { [key: string]: string } = {
      'pendiente': 'Pendiente',
      'confirmado': 'Confirmado',
      'preparando': 'Preparando',
      'enviado': 'En Camino',
      'entregado': 'Entregado',
      'cancelado': 'Cancelado'
    };
    return estadoMap[estado] || estado;
  }
  // Si solo está procesando (no pagado aún)
  if (estadoPago === 'procesando' && !pagado) return 'Pago Procesando';
  if (!pagado && estado === 'pendiente') return 'Pendiente de Pago';
  
  // Mapear estados del pedido
  const estadoMap: { [key: string]: string } = {
    'pendiente': 'Pendiente',
    'confirmado': 'Confirmado',
    'preparando': 'Preparando',
    'enviado': 'En Camino',
    'entregado': 'Entregado',
    'cancelado': 'Cancelado'
  };
  
  return estadoMap[estado] || estado;
};

const CustomerOrders: React.FC = () => {
  const [orders, setOrders] = useState<Pedido[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<Pedido | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [generandoBoleta, setGenerandoBoleta] = useState(false);
  const { user } = useAuth();

  // Función para generar y descargar boleta PDF
  const descargarBoleta = (pedido: Pedido) => {
    setGenerandoBoleta(true);
    
    const fechaPedido = new Date(pedido.fecha_pedido).toLocaleDateString('es-CL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    const itemsRows = pedido.items?.map(item => `
      <tr>
        <td style="padding: 12px; border-bottom: 1px solid #eee;">${item.producto_detalle?.nombre || item.producto_nombre || 'Producto'}</td>
        <td style="padding: 12px; border-bottom: 1px solid #eee; text-align: center;">${item.cantidad}</td>
        <td style="padding: 12px; border-bottom: 1px solid #eee; text-align: right;">${formatPrice(item.precio_unitario)}</td>
        <td style="padding: 12px; border-bottom: 1px solid #eee; text-align: right; font-weight: 600;">${formatPrice(item.precio_total)}</td>
      </tr>
    `).join('') || '';

    const boletaHTML = `
      <!DOCTYPE html>
      <html lang="es">
      <head>
        <meta charset="UTF-8">
        <title>Boleta ${pedido.numero_pedido} - El Piñón</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Segoe UI', Arial, sans-serif; 
            padding: 40px; 
            background: #fff;
            color: #333;
          }
          .boleta-container {
            max-width: 800px;
            margin: 0 auto;
            border: 2px solid #2e5a33;
            border-radius: 16px;
            overflow: hidden;
            box-shadow: 0 10px 40px rgba(0,0,0,0.1);
          }
          .header {
            background: linear-gradient(135deg, #2e5a33 0%, #4a7c59 100%);
            color: white;
            padding: 35px;
            text-align: center;
          }
          .header h1 {
            font-size: 32px;
            margin-bottom: 8px;
          }
          .header p {
            opacity: 0.9;
            font-size: 16px;
          }
          .boleta-info {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 20px;
            padding: 25px 30px;
            background: linear-gradient(135deg, #f8fffe 0%, #e8f5e8 100%);
            border-bottom: 1px solid #e8f5e8;
          }
          .boleta-info div {
            text-align: center;
          }
          .boleta-info label {
            font-size: 11px;
            color: #666;
            text-transform: uppercase;
            letter-spacing: 1.5px;
            display: block;
          }
          .boleta-info p {
            font-size: 15px;
            font-weight: 600;
            color: #2e5a33;
            margin-top: 6px;
          }
          .cliente-info {
            padding: 25px 30px;
            background: #fff;
            border-bottom: 1px solid #e8f5e8;
          }
          .cliente-info h3 {
            color: #2e5a33;
            margin-bottom: 18px;
            font-size: 18px;
          }
          .cliente-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 15px;
          }
          .cliente-grid div label {
            color: #888;
            font-size: 12px;
            display: block;
            margin-bottom: 3px;
          }
          .cliente-grid div p {
            font-size: 14px;
            color: #333;
          }
          .productos {
            padding: 25px 30px;
          }
          .productos h3 {
            color: #2e5a33;
            margin-bottom: 18px;
            font-size: 18px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
          }
          thead tr {
            background: linear-gradient(135deg, #f8fffe 0%, #e8f5e8 100%);
          }
          th {
            padding: 14px 12px;
            text-align: left;
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 1px;
            color: #555;
            border-bottom: 2px solid #2e5a33;
          }
          th:nth-child(2) { text-align: center; }
          th:nth-child(3), th:nth-child(4) { text-align: right; }
          td { font-size: 14px; color: #333; }
          .total-section {
            padding: 25px 30px;
            background: linear-gradient(135deg, #2e5a33 0%, #4a7c59 100%);
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          .total-section span {
            color: white;
            font-size: 18px;
            font-weight: 500;
          }
          .total-section .monto {
            font-size: 32px;
            font-weight: 700;
          }
          .footer {
            padding: 25px 30px;
            text-align: center;
            background: #f8fffe;
            font-size: 13px;
            color: #666;
          }
          .footer p { margin: 6px 0; }
          .footer strong { color: #2e5a33; }
          @media print {
            body { padding: 0; }
            .boleta-container { border: none; box-shadow: none; }
          }
        </style>
      </head>
      <body>
        <div class="boleta-container">
          <div class="header">
            <h1>🌲 El Piñón</h1>
            <p>Boleta de Compra</p>
          </div>

          <div class="boleta-info">
            <div>
              <label>Nº Pedido</label>
              <p>${pedido.numero_pedido}</p>
            </div>
            <div>
              <label>Fecha</label>
              <p>${fechaPedido}</p>
            </div>
            <div>
              <label>Estado</label>
              <p>${getStatusText(pedido.estado, pedido.estado_pago, pedido.pagado)}</p>
            </div>
            <div>
              <label>Método de Pago</label>
              <p>${pedido.metodo_pago || 'No especificado'}</p>
            </div>
          </div>

          <div class="cliente-info">
            <h3>📋 Datos del Cliente</h3>
            <div class="cliente-grid">
              <div>
                <label>Nombre Completo</label>
                <p>${pedido.cliente_detalle ? `${pedido.cliente_detalle.nombre || ''} ${pedido.cliente_detalle.apellido || ''}`.trim() || 'Cliente' : 'Cliente'}</p>
              </div>
              <div>
                <label>Email</label>
                <p>${pedido.cliente_detalle?.email || user?.email || '-'}</p>
              </div>
              <div>
                <label>Teléfono</label>
                <p>${pedido.direccion_entrega_detalle?.telefono_contacto || pedido.cliente_detalle?.telefono || '-'}</p>
              </div>
              <div>
                <label>RUT/DNI</label>
                <p>${pedido.cliente_detalle?.ruc || pedido.cliente_detalle?.dni || '-'}</p>
              </div>
              <div style="grid-column: span 2;">
                <label>Dirección de Entrega</label>
                <p>${pedido.direccion_entrega_detalle ? `${pedido.direccion_entrega_detalle.direccion_linea1}${pedido.direccion_entrega_detalle.direccion_linea2 ? ', ' + pedido.direccion_entrega_detalle.direccion_linea2 : ''}, ${pedido.direccion_entrega_detalle.distrito || ''} ${pedido.direccion_entrega_detalle.ciudad}`.trim() : '-'}</p>
              </div>
            </div>
          </div>

          <div class="productos">
            <h3>🛒 Detalle de Productos</h3>
            <table>
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>Cantidad</th>
                  <th>Precio Unit.</th>
                  <th>Subtotal</th>
                </tr>
              </thead>
              <tbody>
                ${itemsRows}
              </tbody>
            </table>
          </div>

          <div class="total-section">
            <span>TOTAL A PAGAR</span>
            <span class="monto">${formatPrice(pedido.total)}</span>
          </div>

          <div class="footer">
            <p><strong>¡Gracias por tu compra en El Piñón!</strong></p>
            <p>www.elpinon.almacenlocal.com</p>
            ${pedido.codigo_seguimiento ? `<p>Código de seguimiento: <strong>${pedido.codigo_seguimiento}</strong></p>` : ''}
          </div>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(boletaHTML);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.print();
        setGenerandoBoleta(false);
      };
      setTimeout(() => setGenerandoBoleta(false), 2000);
    } else {
      setGenerandoBoleta(false);
      alert('Por favor habilita las ventanas emergentes para descargar la boleta');
    }
  };

  useEffect(() => {
    const fetchOrders = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const fetchedOrders = await getPedidos();
        const ordersArray = Array.isArray(fetchedOrders) ? fetchedOrders : [];
        setOrders(ordersArray);
      } catch (error) {
        console.error('Error fetching orders:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [user]);

  const handleViewDetails = (order: Pedido) => {
    setSelectedOrder(order);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedOrder(null);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Fecha no disponible';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress sx={{ color: '#2e5a33' }} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, color: '#2e5a33' }}>
        Mis Pedidos
      </Typography>

      {orders.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: '15px' }}>
          No tienes pedidos realizados aún. ¡Explora nuestros productos y haz tu primer pedido!
        </Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {orders.map((order) => (
            <OrderCard key={order.id}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      Pedido #{order.numero_pedido}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {formatDate(order.fecha_pedido)}
                    </Typography>
                  </Box>
                  <Chip
                    icon={getStatusIcon(order.estado, order.estado_pago, order.pagado)}
                    label={getStatusText(order.estado, order.estado_pago, order.pagado)}
                    sx={{
                      backgroundColor: getStatusColorBg(order.estado, order.estado_pago, order.pagado),
                      color: 'white',
                      fontWeight: 'bold',
                    }}
                  />
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                  <Box sx={{ flex: '1 1 300px' }}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Productos ({order.items?.length || 0})
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {order.items?.slice(0, 3).map((item, idx) => (
                        <Avatar
                          key={idx}
                          src={item.producto_detalle?.imagen || '/placeholder.png'}
                          alt={item.producto_nombre}
                          sx={{ width: 40, height: 40 }}
                        />
                      ))}
                      {(order.items?.length || 0) > 3 && (
                        <Avatar sx={{ width: 40, height: 40, bgcolor: '#e0e0e0', color: '#666' }}>
                          +{(order.items?.length || 0) - 3}
                        </Avatar>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ flex: '0 1 150px' }}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Total
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2e5a33' }}>
                      {formatPrice(order.total)}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: '0 1 200px' }}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Estado de Pago
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PaymentIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {order.pagado && order.estado_pago === 'aprobado'
                          ? 'Pagado'
                          : order.estado_pago === 'procesando' && !order.pagado
                            ? 'Procesando'
                            : 'Pendiente'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={<ReceiptIcon />}
                      onClick={() => descargarBoleta(order)}
                      disabled={generandoBoleta}
                      sx={{
                        borderColor: '#9c27b0',
                        color: '#9c27b0',
                        '&:hover': {
                          borderColor: '#7b1fa2',
                          backgroundColor: 'rgba(156, 39, 176, 0.04)',
                        },
                      }}
                    >
                      Boleta
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      onClick={() => handleViewDetails(order)}
                      sx={{
                        borderColor: '#2e5a33',
                        color: '#2e5a33',
                        '&:hover': {
                          borderColor: '#1a3d1e',
                          backgroundColor: 'rgba(46, 90, 51, 0.04)',
                        },
                      }}
                    >
                      Ver Detalles
                    </Button>
                  </Box>
                </CardContent>
              </OrderCard>
          ))}
        </Box>
      )}

      {/* Dialog de detalles del pedido */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedOrder && (
          <>
            <DialogTitle>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Detalles del Pedido #{selectedOrder.numero_pedido}
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                    Productos Pedidos
                  </Typography>
                  <List>
                    {selectedOrder.items?.map((item, index) => (
                      <React.Fragment key={index}>
                        <ListItem>
                          <ListItemAvatar>
                            <Avatar src={item.producto_detalle?.imagen || '/placeholder.png'} alt={item.producto_nombre || item.producto_detalle?.nombre || 'Producto'} />
                          </ListItemAvatar>
                          <ListItemText
                            primary={item.producto_nombre || item.producto_detalle?.nombre || `Producto #${item.producto}`}
                            secondary={`Cantidad: ${item.cantidad} - ${formatPrice(item.precio_unitario)} c/u`}
                          />
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                            {formatPrice(item.precio_total)}
                          </Typography>
                        </ListItem>
                        {index < (selectedOrder.items?.length || 0) - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                  <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: '10px' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography>Subtotal:</Typography>
                      <Typography>{formatPrice(selectedOrder.subtotal)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography>Envío:</Typography>
                      <Typography>{formatPrice(selectedOrder.costo_delivery)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography>Descuento:</Typography>
                      <Typography>-{formatPrice(selectedOrder.descuento)}</Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 'bold', textAlign: 'right' }}>
                      Total: {formatPrice(selectedOrder.total)}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                    Estado del Pedido
                  </Typography>
                  <Box sx={{ p: 3, bgcolor: '#f5f5f5', borderRadius: '10px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      {getStatusIcon(selectedOrder.estado, selectedOrder.estado_pago, selectedOrder.pagado)}
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {getStatusText(selectedOrder.estado, selectedOrder.estado_pago, selectedOrder.pagado)}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Fecha de pedido: {formatDate(selectedOrder.fecha_pedido)}
                    </Typography>
                    {selectedOrder.fecha_entrega_estimada && (
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        Entrega estimada: {formatDate(selectedOrder.fecha_entrega_estimada)}
                      </Typography>
                    )}
                    {selectedOrder.fecha_entrega_real && (
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        Entregado: {formatDate(selectedOrder.fecha_entrega_real)}
                      </Typography>
                    )}
                    {selectedOrder.estado === 'entregado' && (
                      <Alert severity="success" sx={{ mt: 2 }}>
                        ¡Tu pedido fue entregado exitosamente!
                      </Alert>
                    )}
                    {selectedOrder.estado === 'cancelado' && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        El pedido fue cancelado.
                      </Alert>
                    )}
                    {selectedOrder.notas_cliente && (
                      <>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
                          Notas:
                        </Typography>
                        <Typography variant="body2">{selectedOrder.notas_cliente}</Typography>
                      </>
                    )}
                  </Box>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button 
                onClick={() => selectedOrder && descargarBoleta(selectedOrder)}
                disabled={generandoBoleta}
                startIcon={generandoBoleta ? <CircularProgress size={16} /> : <ReceiptIcon />}
                sx={{ 
                  color: '#9c27b0',
                  '&:hover': { backgroundColor: 'rgba(156, 39, 176, 0.1)' }
                }}
              >
                {generandoBoleta ? 'Generando...' : 'Descargar Boleta'}
              </Button>
              <Button onClick={handleCloseDialog}>
                Cerrar
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default CustomerOrders;
